import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LiveUpdatesProvider } from './contexts/LiveUpdatesContext';
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import { userNeedsPhone, isShortCourseLearner } from './utils/userProfile';
import SplashScreen from './components/SplashScreen/SplashScreen';
import ScrollToTopButton from './components/ScrollToTopButton';
import FloatingWhatsAppButton from './components/FloatingWhatsAppButton';

const lazyWithRetry = (importer, cacheKey) =>
  lazy(() =>
    importer()
      .then((module) => {
        sessionStorage.removeItem(cacheKey);
        return module;
      })
      .catch((error) => {
        const isChunkLoadError =
          error?.name === 'ChunkLoadError' ||
          /Loading chunk [\w-]+ failed/i.test(error?.message || '');

        if (!isChunkLoadError) {
          throw error;
        }

        if (!sessionStorage.getItem(cacheKey)) {
          sessionStorage.setItem(cacheKey, '1');
          window.location.reload();
          return new Promise(() => {});
        }

        throw error;
      })
  );

// Lazy load components for better performance
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const EducationHub = lazy(() => import('./pages/EducationHub'));
const Payment = lazy(() => import('./pages/Payment'));
const Profile = lazy(() => import('./pages/Profile'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const UploadContent = lazy(() => import('./pages/UploadContent'));
const CreateExam = lazy(() => import('./pages/CreateExam'));
const CreateContentExam = lazy(() => import('./pages/CreateContentExam'));
const MyContent = lazy(() => import('./pages/MyContent'));
const MyExams = lazy(() => import('./pages/MyExams'));
const InstructorEarnings = lazy(() => import('./pages/InstructorEarnings'));
const InstructorAnalytics = lazy(() => import('./pages/InstructorAnalytics'));
const InstructorStudentResults = lazy(() => import('./pages/InstructorStudentResults'));
const InstructorShortCourses = lazy(() => import('./pages/InstructorShortCourses'));
const Content = lazy(() => import('./pages/Content'));
const ContentDetail = lazy(() => import('./pages/ContentDetail'));
const Exams = lazy(() => import('./pages/Exams'));
const Progress = lazy(() => import('./pages/Progress'));
const TakeExam = lazy(() => import('./pages/TakeExam'));
const ExamResults = lazy(() => import('./pages/ExamResults'));
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const Programmes = lazyWithRetry(() => import('./pages/Programmes'), 'retry-programmes-chunk');
const ShortCourses = lazy(() => import('./pages/ShortCourses'));
const ShortCourseDetail = lazy(() => import('./pages/ShortCourseDetail'));
const MyShortCourses = lazy(() => import('./pages/MyShortCourses'));
const ShortCourseLearn = lazy(() => import('./pages/ShortCourseLearn'));
const EventsUpdates = lazy(() => import('./pages/EventsUpdates'));
const FAQ = lazy(() => import('./pages/FAQ'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const CompletePhone = lazy(() => import('./pages/CompletePhone'));
const PaymentCallback = lazy(() => import('./pages/PaymentCallback'));
const MyReport = lazy(() => import('./pages/MyReport'));
const InstructorAgreement = lazy(() => import('./pages/InstructorAgreement'));

const PUBLIC_ROUTES = new Set([
  '/',
  '/home',
  '/about',
  '/services',
  '/programmes',
  '/short-courses',
  '/events',
  '/faq',
  '/privacy-policy',
  '/terms-of-service',
  '/login',
  '/register',
  '/complete-phone',
  '/forgot-password',
  '/reset-password',
]);

const PublicRouteEnhancer = () => {
  const location = useLocation();

  useEffect(() => {
    const isPublic = PUBLIC_ROUTES.has(location.pathname);
    document.body.classList.toggle('public-theme', isPublic);
    return () => {
      document.body.classList.remove('public-theme');
    };
  }, [location.pathname]);

  useEffect(() => {
    const revealEls = document.querySelectorAll('.reveal,.reveal-left,.reveal-right,.reveal-scale');
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      },
      { threshold: 0.12 }
    );

    revealEls.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => {
      const bar = document.getElementById('scroll-progress');
      if (!bar) return;
      const h = document.documentElement;
      const scrollTop = h.scrollTop || document.body.scrollTop;
      const scrollHeight = h.scrollHeight - h.clientHeight;
      const width = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
      bar.style.width = `${Math.max(0, Math.min(100, width))}%`;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname]);

  return <div id="scroll-progress" className="scroll-progress" aria-hidden="true" />;
};

// Loading component
const LoadingSpinner = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    flexDirection: 'column',
    gap: '1rem'
  }}>
    <div style={{
      width: '50px',
      height: '50px',
      border: '4px solid #f3f3f3',
      borderTop: '4px solid #3498db',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }}></div>
    <p style={{ color: '#666', fontSize: '14px' }}>Loading...</p>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

const AppRoutes = () => {
  const { isAuthenticated, loading, user } = useAuth();

  const authenticatedHome = () => {
    if (userNeedsPhone(user)) {
      return <Navigate to="/complete-phone" replace />;
    }
    if (isShortCourseLearner(user)) {
      return <Navigate to="/my-short-courses" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  };

  // Show loading spinner while checking auth status
  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
      {/* Splash Screen - Landing Page (for non-authenticated users) */}
      <Route 
        path="/" 
        element={isAuthenticated ? authenticatedHome() : <SplashScreen />} 
      />

      <Route
        path="home"
        element={<SplashScreen />}
      />
      
      {/* Public routes (outside Layout) */}
      <Route
        path="about"
        element={<About />}
      />
      <Route
        path="services"
        element={<Services />}
      />
      <Route
        path="programmes"
        element={<Programmes />}
      />
      <Route
        path="short-courses"
        element={<ShortCourses />}
      />
      <Route
        path="short-courses/:slug"
        element={<ShortCourseDetail />}
      />
      <Route
        path="events"
        element={<EventsUpdates />}
      />
      <Route
        path="faq"
        element={<FAQ />}
      />
      <Route
        path="login"
        element={isAuthenticated ? authenticatedHome() : <Login />}
      />
      <Route
        path="register"
        element={isAuthenticated ? authenticatedHome() : <Register />}
      />
      <Route
        path="complete-phone"
        element={
          <ProtectedRoute skipPhoneCheck>
            <CompletePhone />
          </ProtectedRoute>
        }
      />
      <Route
        path="forgot-password"
        element={isAuthenticated ? authenticatedHome() : <ForgotPassword />}
      />
      <Route
        path="reset-password"
        element={isAuthenticated ? authenticatedHome() : <ResetPassword />}
      />
      
      {/* Protected routes with Layout */}
      <Route element={<Layout />}>
        <Route
          path="dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="education-hub"
          element={
            <ProtectedRoute>
              <EducationHub />
            </ProtectedRoute>
          }
        />
        <Route
          path="payment"
          element={
            <ProtectedRoute>
              <Payment />
            </ProtectedRoute>
          }
        />
        <Route
          path="payment/callback"
          element={
            <ProtectedRoute>
              <PaymentCallback />
            </ProtectedRoute>
          }
        />
        <Route
          path="profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="privacy-policy"
          element={<PrivacyPolicy />}
        />
        <Route
          path="terms-of-service"
          element={<TermsOfService />}
        />
        <Route
          path="upload-content"
          element={
            <ProtectedRoute>
              <UploadContent />
            </ProtectedRoute>
          }
        />
        <Route
          path="instructor-agreement"
          element={
            <ProtectedRoute>
              <InstructorAgreement />
            </ProtectedRoute>
          }
        />
        <Route
          path="create-exam"
          element={
            <ProtectedRoute>
              <CreateExam />
            </ProtectedRoute>
          }
        />
        <Route
          path="my-content"
          element={
            <ProtectedRoute>
              <MyContent />
            </ProtectedRoute>
          }
        />
        <Route
          path="my-exams"
          element={
            <ProtectedRoute>
              <MyExams />
            </ProtectedRoute>
          }
        />
        <Route
          path="content"
          element={
            <ProtectedRoute>
              <Content />
            </ProtectedRoute>
          }
        />
        <Route
          path="content/:id"
          element={
            <ProtectedRoute>
              <ContentDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="content/:id/create-exam"
          element={
            <ProtectedRoute>
              <CreateContentExam />
            </ProtectedRoute>
          }
        />
        <Route
          path="instructor/earnings"
          element={
            <ProtectedRoute>
              <InstructorEarnings />
            </ProtectedRoute>
          }
        />
        <Route
          path="instructor/analytics"
          element={
            <ProtectedRoute>
              <InstructorAnalytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="instructor/short-courses"
          element={
            <ProtectedRoute>
              <InstructorShortCourses />
            </ProtectedRoute>
          }
        />
        <Route
          path="instructor/student-results"
          element={
            <ProtectedRoute>
              <InstructorStudentResults />
            </ProtectedRoute>
          }
        />
        <Route
          path="exams"
          element={
            <ProtectedRoute>
              <Exams />
            </ProtectedRoute>
          }
        />
        <Route
          path="exams/:id/take"
          element={
            <ProtectedRoute>
              <TakeExam />
            </ProtectedRoute>
          }
        />
        <Route
          path="progress"
          element={
            <ProtectedRoute>
              <Progress />
            </ProtectedRoute>
          }
        />
        <Route
          path="my-short-courses"
          element={
            <ProtectedRoute>
              <MyShortCourses />
            </ProtectedRoute>
          }
        />
        <Route
          path="short-courses/:slug/learn"
          element={
            <ProtectedRoute>
              <ShortCourseLearn />
            </ProtectedRoute>
          }
        />
        <Route
          path="reports"
          element={
            <ProtectedRoute>
              <MyReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="exam-results/:id"
          element={
            <ProtectedRoute>
              <ExamResults />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LiveUpdatesProvider>
          <PublicRouteEnhancer />
          <AppRoutes />
          <ScrollToTopButton />
          <FloatingWhatsAppButton />
        </LiveUpdatesProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
